// TopbarStepper — renders a <StepIsland> into the shared AppShell top-bar slot
// (BO UI Standard §5: the Island is "docked in the top banner", centred between
// the breadcrumb and the right-hand actions).  A form computes its own steps
// and renders <TopbarStepper steps={…}/> anywhere in its JSX; the portal moves
// the pill up into the top bar.  Only one workflow view is mounted at a time, so
// there is never more than one island in the slot.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StepIsland, type IslandStep } from './StepIsland';

/** The id of the top-bar slot rendered by AppShell. */
export const TOPBAR_STEPPER_SLOT_ID = 'ai-topbar-stepper';

/** Props are StepIsland's own, minus `className`: the docked island's look is
 *  the standard's, not the caller's. `onStepClick` / `isClickable` are passed
 *  straight through so a docked stepper can be navigable — a purely visual one
 *  simply omits them, exactly as before. */
export function TopbarStepper({ steps, onStepClick, isClickable }: {
  steps: IslandStep[] | null;
  onStepClick?: (id: string) => void;
  isClickable?: (step: IslandStep) => boolean;
}) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  // Grab the slot after mount — the DOM node is committed by AppShell before the
  // content (and this component) run their effects.
  useEffect(() => {
    setNode(document.getElementById(TOPBAR_STEPPER_SLOT_ID));
  }, []);
  if (!node || !steps || steps.length === 0) return null;
  return createPortal(
    <StepIsland steps={steps} onStepClick={onStepClick} isClickable={isClickable} />,
    node);
}
